import * as d3 from 'd3';

export type IcicleNode = {
  id: string;
  parentId: string | null;
  name: string;
  weight?: number;
  isOther?: boolean;
  ownWeight?: number;
};

export type SerializedIcicleNode = {
  id: string;
  parentId: string | null;
  name: string;
  depth: number;
  value: number | undefined;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  isOther?: boolean;
};

type InternalHierarchyNode = {
  id: string;
  name: string;
  weight?: number;
  isOther?: boolean;
  ownWeight?: number;
  children?: InternalHierarchyNode[];
};

const ICICLE_ROOT_ID = '__icicle_root__';
const ICICLE_ROOT_NAME = 'All';
export const ICICLE_DEFAULT_WIDTH_RATIO = 3.4;

function buildIcicleHierarchy(
  data: readonly IcicleNode[]
): InternalHierarchyNode {
  const itemById = new Map<string, IcicleNode>();
  for (const item of data) {
    if (!itemById.has(item.id)) itemById.set(item.id, item);
  }

  const reachesItself = (item: IcicleNode): boolean => {
    const seen = new Set<string>([item.id]);
    let parentId = item.parentId;
    while (parentId !== null) {
      if (seen.has(parentId)) return true;
      seen.add(parentId);
      parentId = itemById.get(parentId)?.parentId ?? null;
    }
    return false;
  };

  const nodeById = new Map<string, InternalHierarchyNode>();
  for (const item of itemById.values()) {
    nodeById.set(item.id, {
      id: item.id,
      name: item.name,
      weight: item.weight,
      isOther: item.isOther,
      ownWeight: item.ownWeight,
    });
  }
  const root: InternalHierarchyNode = {
    id: ICICLE_ROOT_ID,
    name: ICICLE_ROOT_NAME,
    children: [],
  };
  for (const item of itemById.values()) {
    const node = nodeById.get(item.id);
    if (!node) continue;
    const parent =
      item.parentId !== null && !reachesItself(item)
        ? nodeById.get(item.parentId)
        : undefined;
    const container = parent ?? root;
    container.children = container.children ?? [];
    container.children.push(node);
  }
  return root;
}

function applyWidthScaling(
  root: d3.HierarchyRectangularNode<InternalHierarchyNode>,
  fullWidth: number,
  ratio: number
): void {
  const maxDepth = root.height;
  const depthExtents: { min: number; max: number }[] = Array.from(
    { length: maxDepth + 1 },
    () => ({ min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY })
  );

  root.each((node) => {
    const ext = depthExtents[node.depth];
    if (!ext) return;
    if (node.y0 < ext.min) ext.min = node.y0;
    if (node.y1 > ext.max) ext.max = node.y1;
  });

  const zoneShift: number[] = [];
  const zoneWidth: number[] = [];
  let remaining = fullWidth;
  let used = 0;
  for (let d = 0; d <= maxDepth; d++) {
    const zw = remaining / ratio;
    zoneShift[d] = used;
    zoneWidth[d] = zw;
    used += zw;
    remaining -= zw;
    if (remaining < 0.5) remaining = 0;
  }

  const rootOffset = zoneWidth[0] ?? 0;
  zoneShift.forEach((shift, idx) => {
    if (idx >= 1) zoneShift[idx] = shift - rootOffset;
  });

  root.each((node) => {
    const ext = depthExtents[node.depth];
    if (!ext) return;
    const range = ext.max - ext.min || 1;
    const normY0 = (node.y0 - ext.min) / range;
    const normY1 = (node.y1 - ext.min) / range;
    const shift = zoneShift[node.depth] ?? 0;
    const zw = zoneWidth[node.depth] ?? 0;
    node.y0 = shift + normY0 * zw;
    node.y1 = shift + normY1 * zw;
  });
}

export type IcicleSizeTransform = 'none' | 'sqrt';

function applySqrtSizing(root: d3.HierarchyNode<InternalHierarchyNode>): void {
  const rawValue = new Map<d3.HierarchyNode<InternalHierarchyNode>, number>();
  root.each((node) => {
    rawValue.set(node, Math.max(0, node.value ?? 0));
  });
  const sqrtOf = (node: d3.HierarchyNode<InternalHierarchyNode>) =>
    Math.sqrt(rawValue.get(node) ?? 0);
  const sqrtOfChildren = (node: d3.HierarchyNode<InternalHierarchyNode>) =>
    (node.children ?? []).reduce((sum, child) => sum + sqrtOf(child), 0);
  (root as { value?: number }).value = Math.max(
    sqrtOf(root),
    sqrtOfChildren(root)
  );
  root.eachBefore((node) => {
    const children = node.children;
    if (children === undefined || children.length === 0) return;
    const wanted = sqrtOfChildren(node);
    const available = node.value ?? 0;
    const scale = wanted > available && wanted > 0 ? available / wanted : 1;
    for (const child of children) {
      (child as { value?: number }).value = sqrtOf(child) * scale;
    }
  });
}

export type IcicleSiblingOrder = 'deepest-first' | 'largest-first';

type HierarchyEntry = d3.HierarchyNode<InternalHierarchyNode>;

const otherLast = (a: HierarchyEntry, b: HierarchyEntry) =>
  Number(a.data.isOther === true) - Number(b.data.isOther === true);

const largerFirst = (a: HierarchyEntry, b: HierarchyEntry) =>
  (b.value ?? 0) - (a.value ?? 0);

const SIBLING_COMPARATORS: Record<
  IcicleSiblingOrder,
  (a: HierarchyEntry, b: HierarchyEntry) => number
> = {
  'deepest-first': (a, b) =>
    otherLast(a, b) || b.height - a.height || largerFirst(a, b),
  'largest-first': (a, b) => otherLast(a, b) || largerFirst(a, b),
};

function buildIcicleHierarchyRoot(
  data: readonly IcicleNode[],
  sizeTransform: IcicleSizeTransform,
  siblingOrder: IcicleSiblingOrder = 'deepest-first'
): HierarchyEntry {
  const rootBase = d3
    .hierarchy<InternalHierarchyNode>(
      buildIcicleHierarchy(data),
      (d) => d.children
    )
    .sum((d) => {
      if (!d.children || d.children.length === 0) return d.weight ?? 1;
      return d.ownWeight ?? 0;
    })
    .sort(SIBLING_COMPARATORS[siblingOrder]);
  if (sizeTransform === 'sqrt') applySqrtSizing(rootBase);
  return rootBase;
}

export function computeIcicleLayout(
  data: readonly IcicleNode[],
  width: number,
  height: number,
  widthRatio: number = ICICLE_DEFAULT_WIDTH_RATIO,
  sizeTransform: IcicleSizeTransform = 'none',
  siblingOrder: IcicleSiblingOrder = 'deepest-first'
): SerializedIcicleNode[] {
  if (width <= 0 || height <= 0 || data.length === 0) return [];

  const rootBase = buildIcicleHierarchyRoot(data, sizeTransform, siblingOrder);

  const partition = d3.partition<InternalHierarchyNode>().size([height, width]);
  const root = partition(
    rootBase as unknown as d3.HierarchyRectangularNode<InternalHierarchyNode>
  );

  applyWidthScaling(root, width, widthRatio);

  const flat: SerializedIcicleNode[] = [];
  root.each((node) => {
    flat.push({
      id: node.data.id,
      parentId: node.parent ? node.parent.data.id : null,
      name: node.data.name,
      depth: node.depth,
      value: node.value,
      x0: node.x0,
      x1: node.x1,
      y0: node.y0,
      y1: node.y1,
      ...(node.data.isOther === true ? { isOther: true } : {}),
    });
  });
  return flat;
}

const MIN_LEVEL_WIDTH = 56;

const sum = (values: readonly number[]) =>
  values.reduce((total, value) => total + value, 0);

function fitLevelWidths(
  widths: readonly number[],
  needs: readonly number[],
  total: number
): number[] {
  const wants = widths.map((width, level) =>
    Math.min(
      total,
      Math.max(needs[level] ?? 0, Math.min(width, MIN_LEVEL_WIDTH))
    )
  );
  if (wants.every((want, level) => want <= (widths[level] ?? 0))) {
    return [...widths];
  }
  const raised = widths.map((width, level) =>
    Math.max(width, wants[level] ?? 0)
  );
  const excess = sum(raised) - total;
  const surplus = raised.map((width, level) => width - (wants[level] ?? 0));
  const available = sum(surplus);
  if (available >= excess) {
    return raised.map(
      (width, level) => width - ((surplus[level] ?? 0) * excess) / available
    );
  }
  const fair = [...wants];
  let remaining = total;
  let left = wants.length;
  for (const level of wants
    .map((_, index) => index)
    .sort((a, b) => (wants[a] ?? 0) - (wants[b] ?? 0))) {
    const share = Math.min(wants[level] ?? 0, remaining / left);
    fair[level] = share;
    remaining -= share;
    left -= 1;
  }
  return fair;
}

export function fitIcicleLevelsToLabels(
  nodes: SerializedIcicleNode[],
  width: number,
  labelSpace: (node: SerializedIcicleNode) => number
): SerializedIcicleNode[] {
  const bands = new Map<number, { y0: number; y1: number; need: number }>();
  for (const node of nodes) {
    if (node.depth === 0) continue;
    const band = bands.get(node.depth) ?? {
      y0: node.y0,
      y1: node.y1,
      need: 0,
    };
    band.need = Math.max(band.need, labelSpace(node));
    bands.set(node.depth, band);
  }
  const depths = [...bands.keys()].sort((a, b) => a - b);
  if (depths.length < 2 || width <= 0) return nodes;

  const levels = depths.flatMap((depth) => bands.get(depth) ?? []);
  const start = Math.min(...levels.map((band) => band.y0));
  const extent = Math.max(...levels.map((band) => band.y1)) - start;
  if (extent <= 0) return nodes;

  const current = levels.map((band) => ((band.y1 - band.y0) / extent) * width);
  const fitted = fitLevelWidths(
    current,
    levels.map((band) => band.need),
    width
  );
  if (fitted.every((level, index) => level === current[index])) return nodes;

  const nextBand = new Map<number, { y0: number; y1: number }>();
  let offset = 0;
  depths.forEach((depth, index) => {
    const size = ((fitted[index] ?? 0) / width) * extent;
    nextBand.set(depth, { y0: start + offset, y1: start + offset + size });
    offset += size;
  });
  return nodes.map((node) => {
    const band = nextBand.get(node.depth);
    return band === undefined ? node : { ...node, ...band };
  });
}

export function computeFocusTransform(
  nodes: readonly SerializedIcicleNode[],
  focusId: string | null,
  width: number,
  height: number
): SerializedIcicleNode[] {
  if (nodes.length === 0) return [];

  const byId = new Map(nodes.map((node) => [node.id, node]));
  const rootId = nodes.find((node) => node.depth === 0)?.id ?? null;
  const resolvedFocusId =
    focusId !== null && byId.has(focusId) ? focusId : rootId;

  if (resolvedFocusId === null) return nodes.slice();

  const isDescendant = (node: SerializedIcicleNode): boolean => {
    const seen = new Set<string>();
    let current: SerializedIcicleNode | undefined = node;
    while (current && !seen.has(current.id)) {
      if (current.id === resolvedFocusId) return true;
      seen.add(current.id);
      current =
        current.parentId !== null ? byId.get(current.parentId) : undefined;
    }
    return false;
  };

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const node of nodes) {
    if (node.depth === 0 || !isDescendant(node)) continue;
    if (node.x0 < minX) minX = node.x0;
    if (node.x1 > maxX) maxX = node.x1;
    if (node.y0 < minY) minY = node.y0;
    if (node.y1 > maxY) maxY = node.y1;
  }
  if (!Number.isFinite(minX)) return nodes.slice();

  const scaleX = height / (maxX - minX || 1);
  const scaleY = width / (maxY - minY || 1);
  const offsetX = -minX * scaleX;
  const offsetY = -minY * scaleY;

  return nodes.map((node) => ({
    ...node,
    x0: node.x0 * scaleX + offsetX,
    x1: node.x1 * scaleX + offsetX,
    y0: node.y0 * scaleY + offsetY,
    y1: node.y1 * scaleY + offsetY,
  }));
}

export const ICICLE_GUTTER = 3;
export const ICICLE_SEPARATOR = 1;
const MIN_SEPARATED_HEIGHT = 3;
const MIN_GUTTERED_HEIGHT = 8;

type IcicleBand = Pick<SerializedIcicleNode, 'x0' | 'x1' | 'y0' | 'y1'>;

export function icicleCellRect(
  node: IcicleBand,
  branch: IcicleBand,
  width: number,
  height: number,
  previousBottom?: number
): { x: number; y: number; w: number; h: number } {
  const x = Math.round(node.y0);
  const right = Math.round(
    node.y1 < width - 0.5 ? node.y1 - ICICLE_GUTTER : node.y1
  );
  const start = Math.round(node.x0);
  const end = Math.round(node.x1);
  const y =
    previousBottom !== undefined &&
    previousBottom >= start &&
    end - start > ICICLE_SEPARATOR
      ? start + ICICLE_SEPARATOR
      : start;
  const branchEnd = Math.round(branch.x1);
  const branchHeight = branchEnd - Math.round(branch.x0);
  const branchGutter = (() => {
    if (branch.x1 >= height - 0.5 || branchHeight < MIN_SEPARATED_HEIGHT)
      return 0;
    return branchHeight >= MIN_GUTTERED_HEIGHT
      ? ICICLE_GUTTER
      : ICICLE_SEPARATOR;
  })();
  const branchBottom = branchEnd - branchGutter;
  const separator = end - start >= MIN_SEPARATED_HEIGHT ? ICICLE_SEPARATOR : 0;
  const bottom = end >= branchEnd ? branchBottom : end - separator;
  return { x, y, w: right - x, h: Math.min(bottom, branchBottom) - y };
}
